import { api } from '../services/axiosConfig';
import { SdeSolarSystem, WhEffect, WhSiteDetail } from '../types';

export interface SiteBucketOverview {
  className: string;
  category: 'gas' | 'ore';
  names: string[];
}

export interface SiteClassOverview {
  className: string;
  combatSites: string[];
  relicDataSites: string[];
  rats: string;
}

export const fetchSiteOverview = (className: string) =>
  api.get<SiteBucketOverview | SiteClassOverview>(`/api/sites/${encodeURIComponent(className)}`).then((r) => r.data);

/** The exact names a `site` or `wh_class` fitting tag has to use to reach
 * the page it is meant for — offered as picks so nobody has to spell
 * "Forgotten Frontier Quarantine Outpost" from memory. */
export interface SiteNameIndex {
  whClasses: string[];
  sites: string[];
}

export const fetchSiteNames = () => api.get<SiteNameIndex>('/api/site-names').then((r) => r.data);

/** Share of a full volley by damage type, in percent. */
export interface DamageProfile {
  em: number;
  explosive: number;
  kinetic: number;
  thermal: number;
}

/** Per-hull combat stats, derived from CCP's own dogma.
 * Mirrors src/data/sleeperShips.ts. */
export interface SleeperShipStats {
  name: string;
  typeId: number;
  sig: number | null;
  speed: number | null;
  targetRangeKm: number | null;
  orbitKm: number | null;
  orbitSpeed: number | null;
  turretDps: number;
  turretVolley: number;
  turretInterval: number;
  missileDps: number;
  missileVolley: number;
  missileInterval: number;
  ehp: number;
  /** EHP against each damage type alone. Nearly every Sleeper is flat across
   * all four; the handful that are not is the whole reason this exists. */
  ehpByType: DamageProfile;
  damageProfile: DamageProfile | null;
}

/** Sleepers fire a turret AND a missile weapon; what lands on you is the
 * sum. Kept as helpers rather than baked into the payload so the two halves
 * stay separately readable in the per-ship row. */
export const shipDps = (s: SleeperShipStats) => s.turretDps + s.missileDps;
export const shipVolley = (s: SleeperShipStats) => s.turretVolley + s.missileVolley;

/** Damage and tank for a group of ships, at worst-case counts. */
export interface NpcTotals {
  dps: number;
  volley: number;
  ehp: number;
  /** False when a ship in the group had no stats — every number is a floor. */
  complete: boolean;
}

/** One NPC hull in a wave. Mirrors src/data/parseNpcWaves.ts. */
export interface NpcShip {
  count: string;
  countMin: number;
  countMax: number;
  name: string;
  hull: 'tower' | 'frigate' | 'destroyer' | 'cruiser' | 'battlecruiser' | 'battleship' | 'unknown';
  ewar: string[];
  orbit: string | null;
  isTrigger: boolean;
  stats: SleeperShipStats | null;
}

export interface NpcWave {
  label: string;
  note: string | null;
  ships: NpcShip[];
  totalMin: number;
  totalMax: number;
  isEscalation: boolean;
  totals: NpcTotals;
  /** What THIS wave hits with, DPS-weighted. Differs wave to wave, which is
   * exactly why the site-level average can't be the number you set resists
   * to. Null when no hull in the wave has stats. */
  damageProfile: DamageProfile | null;
}

/** The site in four numbers. `peak` is the worst SINGLE wave — waves are
 * sequential, so that is the moment your tank has to survive. `allWaves` is
 * the ceiling if everything ends up alive at once, which is what a
 * chain-trigger does. Escalations are excluded from both and summarised
 * separately. */
export interface SiteThreat {
  peak: NpcTotals;
  peakWave: string | null;
  allWaves: NpcTotals;
  escalation: NpcTotals | null;
  waveCount: number;
  /** How many waves escalate when something in them dies — NOT a count of
   * trigger ships. */
  triggerCount: number;
  ewar: string[];
  /** The site's real incoming damage mix, weighted by each hull's DPS. */
  damageProfile: DamageProfile | null;
}

/** Jita 4-4 buy/sell from the daily poller's cache. Either side is null when
 * that side of the book was empty. */
export interface MarketPrice {
  typeId: number;
  buy: number | null;
  sell: number | null;
  updatedAt: string;
}

export interface ResourcePrice extends MarketPrice {
  /** The type actually priced, which for a renamed ore/ice variant is the
   * base ore rather than the catalogue's name. */
  typeName: string;
  viaBaseOre: boolean;
}

export interface BlueLootPrice extends MarketPrice {
  name: string;
}

export interface SiteDetailResponse {
  className: string;
  siteName: string;
  detail: WhSiteDetail | null;
  /** Structured form of `detail.npcComposition`. Null when the string could
   * not be fully parsed — render the raw sentence in that case rather than a
   * table that might be missing a wave. */
  waves: NpcWave[] | null;
  /** Null for exactly the sites whose `waves` is null. */
  threat: SiteThreat | null;
  /** Keyed by resource name as the catalogue writes it. Absent for a
   * resource with no priced type — render "—", never a zero. */
  resourcePrices: Record<string, ResourcePrice>;
  /** Sleeper components, on combat/relic/data sites only. */
  blueLootPrices: BlueLootPrice[];
  system: SdeSolarSystem | null;
  systemEffect: WhEffect | null;
}

export const fetchSiteDetail = (className: string, siteName: string, systemId?: number) =>
  api
    .get<SiteDetailResponse>(`/api/sites/${encodeURIComponent(className)}/${encodeURIComponent(siteName)}`, {
      params: systemId ? { system: systemId } : undefined,
    })
    .then((r) => r.data);

export type SightingCategory = 'combat' | 'relic' | 'data' | 'gas' | 'ore';

export interface SiteSighting {
  id: number;
  category: SightingCategory;
  site_name: string;
  created_at: string;
  expires_at: string;
  reported_by: string;
}

export const fetchSightings = (solarSystemId: number) =>
  api.get<SiteSighting[]>(`/api/systems/${solarSystemId}/sightings`).then((r) => r.data);

export const reportSighting = (solarSystemId: number, category: SightingCategory, siteName: string, ttlHours?: number) =>
  api.post<{ id: number }>(`/api/systems/${solarSystemId}/sightings`, { category, siteName, ttlHours }).then((r) => r.data);

/** "It's done" — clears every active report of this site ahead of its
 * natural expiry. No webhook fires for this (see the route's own comment):
 * removing a badge isn't new information. Only ever clears reports your own
 * corp/alliance could already see. */
export const clearSightings = (solarSystemId: number, category: SightingCategory, siteName: string) =>
  api
    .delete<{ cleared: number }>(`/api/systems/${solarSystemId}/sightings`, { params: { category, siteName } })
    .then((r) => r.data);
